import { Modal } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import {
  IActivityDetail,
  IActivityIndividualItem,
  IExpiryList,
} from "../data-handling/finance-inventory-types";
import BatchQuantityEditModal from "./batch-quantity-edit-modal";
import {
  ACTIVITY_DETAIL_TABLE_HEADER_KEYS,
  ACTIVITY_EDIT_TABLE_HEADER_KEYS,
  INVENTORY_ACTIVITY_STATUS,
  INVENTORY_ACTIVITY_TYPES,
} from "../data-handling/finance-inventory-constants";
import useFinanceInventory from "../data-handling/use-finance-inventory";
import { decimalPriceRegex } from "../../../utils";
import CtaComponent from "../../../components/reusable-ui/cta";
import { CTA_SIZE, CTA_TYPE,DATE_TIME_FORMATS } from "../../../data/constants";
import { cloneDeep } from "lodash";
import { isPostFinalApprovedActivity } from "../utils/common";
import DateDropdown from "../../../components/reusable-ui/date-picker-dropdown";
import { DatePicker, Select } from "antd";
import moment, { Moment } from "moment";
import { currency } from "../../../helpers/common-helper";

type ModalProps = {
  open: boolean;
  onClose: () => void;
};
const currencySymbol = currency();
function ItemsEditModal({ open, onClose }: ModalProps) {
  const { activityDetail, editPostFinalData, selectedActivityRow, saveInventoryActivity } =
    useFinanceInventory();
  const [batchToEdit, setBatchToEdit] = useState<IExpiryList[]>([]);
  const [invoiceNumber, setInvoiceNumber] = useState<any>(null)
  const [invoiceDate, setInvoiceDate] = useState<any>(null);
  const [openBatchQuantityEditModal, setOpenBatchQuantityEditModal] = useState<{
    visible: boolean;
    itemIDForBatchEdit: string;
    parentItem: IActivityIndividualItem | undefined;
  }>({
    visible: false,
    itemIDForBatchEdit: "",
    parentItem: undefined,
  });
  const [editedActivities, setEditedActivities] = useState<IActivityIndividualItem[]>([])

  useEffect(() => {
    open && setEditedActivities(cloneDeep(activityDetail?.items) || []);
    setInvoiceNumber(activityDetail?.supplierInvoiceNumber ?? null);
    setInvoiceDate(activityDetail?.supplierInvoiceDate ?? null);
  }, [activityDetail?.items, open])

  const onChangeCost = (
    itemId: string,
    newValStr: string,
  ) => {
    const decimalStrVal = decimalPriceRegex(newValStr);
    let modifledEditedItems = editedActivities.map((item) => {
      if (item.skuCode === itemId) {
        item.unitCost = decimalStrVal
      }
      return item;
    })

    setEditedActivities(modifledEditedItems)
  };

  const onBatchSave = (batch: IExpiryList[], quantity: number) => {
    let modifledEditedItems = editedActivities.map((item) => {
      if (item.skuCode === openBatchQuantityEditModal.itemIDForBatchEdit) {
        item.expiryList = batch
        item.itemQty = quantity
      }
      return item;
    })
    setEditedActivities(modifledEditedItems)
  };

  const getTableCell = (columnName: string, item: IActivityIndividualItem) => {
    switch (columnName) {
      case ACTIVITY_DETAIL_TABLE_HEADER_KEYS.name:
        return (
          <td className="p-10">
            <div className="flex items-center gap-8">
              <div>
                <div className="text-14 font-500">{item.itemName}</div>
                <div className="text-12 mt-5 text-grey6C">
                  SKU: {item.skuCode}
                </div>
              </div>
            </div>
          </td>
        );
      case ACTIVITY_DETAIL_TABLE_HEADER_KEYS.quantity:
        return (
          <td className="text-center">
            <button
              onClick={() => {
                setOpenBatchQuantityEditModal({
                  itemIDForBatchEdit: item.skuCode,
                  parentItem: item,
                  visible: true,
                });
                setBatchToEdit(item?.expiryList || []);
              }}
              className="border border-1 rounded-[8px] w-80 px-8 py-4 text-center overflow-hidden text-ellipsis"
            >
              {item.itemQty}
            </button>
            <span className="ml-8">{item.measuringUnit}</span>
          </td>
        );
      case ACTIVITY_DETAIL_TABLE_HEADER_KEYS.rate:
        return (
          <td className="text-center">
            <span className="mr-8">{currencySymbol}</span>
            <input
              className="border border-1 rounded-[8px] px-8 py-4 w-80 text-center"
              value={item.unitCost}
              inputMode="decimal"
              onChange={(e) =>
                onChangeCost(
                  item.skuCode,
                  e.target.value
                )
              }
            />
          </td>
        );
      default:
        return <></>;
    }
  };

  const saveCtaConfigFunction = () => {
    const activityDetailCopy = cloneDeep(activityDetail)
    let ctaText = "";
    let action = () => {};
    let disabled = false

    // unitCost is in string as it gets taken from input field, converting it to number before sending to backend
    const reconciledEditedActivities = editedActivities.map((item) => {
      if ((item.unitCost as any) === "" || Number(item.unitCost) < 0 || isNaN(item.unitCost)) {
        disabled = true
      }

      return ({...item, unitCost: Number(item.unitCost)})
    })

    const isApprovedActivity = isPostFinalApprovedActivity(activityDetail as IActivityDetail);

    if (activityDetailCopy) {
      activityDetailCopy.itemActivities = reconciledEditedActivities || []
      activityDetailCopy.items = reconciledEditedActivities || []
      activityDetailCopy.supplierInvoiceNumber = invoiceNumber;
      activityDetailCopy.supplierInvoiceDate = invoiceDate;
      if (isApprovedActivity) {
        ctaText = "Save"
        action = () => editPostFinalData(activityDetailCopy)
      } else {
        ctaText = "Done"
        action = () => saveInventoryActivity(activityDetailCopy)
      }
    }
    return {
      ctaText,
      action,
      disabled,
    };
  };

  const handleInvoiceNumber = (val: any) => {
    setInvoiceNumber(val);
  }

  const onDateRangeSubmission = (date: any) => {
      setInvoiceDate(date);
    };


  const saveCtaConfig = saveCtaConfigFunction()

  return (
    <Modal
      className="flex justify-center items-center"
      open={open}
      onClose={onClose}
    >
      <div className="bg-white min-w-[720px] rounded-[8px] overflow-hidden p-16">
        <h1 className="font-600 text-lg mb-12">Edit Items</h1>
        <BatchQuantityEditModal
          open={openBatchQuantityEditModal.visible}
          onClose={() => setOpenBatchQuantityEditModal({...openBatchQuantityEditModal, visible: false})}
          itemUnderEdit={openBatchQuantityEditModal.parentItem}
          batchToEdit={batchToEdit || []}
          onSave={onBatchSave}
        />

        <div className="mb-12">
          <form className="flex flex-col gap-6">
            <div className="flex items-center gap-4">
              <label className="w-120 block text-sm font-medium text-gray-700 mb-4">
                Invoice Number
              </label>
              <input
                id="invoiceNumber"
                value={invoiceNumber}
                onChange={(e) => handleInvoiceNumber(e.target.value)}
                className={`text-[16px] font-normal h-40 w-[30%] border border-[#C4C4C4] rounded-[8px] px-[16px] py-[9px] mt-[6px] outline-none focus:border-black hover:border-black
              `}
                placeholder="Enter invoice number"
              />
            </div>

            <div className="flex items-center gap-6">
              <label className="w-120 block text-sm font-medium text-gray-700 mb-4">
                Invoice Date
              </label>
              <DatePicker
              popupClassName={" z-[9999]"}
              inputReadOnly
              className={`custom-date-picker h-40 w-[30%] cursor-pointer smd:!ml-0 smd:mt-0 smd:items-center tb:w-[48%] sm:w-[98%] rounded-[8px]
            hover:border-black
            focus:border-black
            border-[#C4C4C4]
          `}
                format={DATE_TIME_FORMATS.dayMonthYear}
                value={invoiceDate ? moment.utc(invoiceDate) : null}
                onChange={onDateRangeSubmission}
              />
            </div>
          </form>
        </div>

        <table className="w-full border text-13 mb-12">
          <thead>
            <tr className="bg-greyF9">
              {Object.keys(ACTIVITY_EDIT_TABLE_HEADER_KEYS).map(
                (columnName) => {
                  if (columnName === ACTIVITY_EDIT_TABLE_HEADER_KEYS.name) {
                    return <th className="text-left p-10 text-xs font-500 text-[#7F7F7F]">ITEMS & DESCRIPTION</th>
                  }
                  return <th className="p-10 text-xs font-500 text-[#7F7F7F]">{(columnName || "").toUpperCase()}</th>;
                }
              )}
            </tr>
          </thead>
          <tbody>
            {editedActivities?.map((item) => (
              <tr key={item.skuCode} className="border-t border-greyEB">
                {Object.keys(ACTIVITY_EDIT_TABLE_HEADER_KEYS).map(
                  (columnName) => {
                    return getTableCell(columnName, item);
                  }
                )}
              </tr>
            ))}
          </tbody>
        </table>

        <div className="flex justify-end">
          <CtaComponent
            ctaText={saveCtaConfig.ctaText}
            ctaType={CTA_TYPE.active_cta}
            ctaSize={CTA_SIZE.small}
            onCtaClick={() => {
              saveCtaConfig.action()
              onClose()
            }}
            isDisabled={saveCtaConfig.disabled}
            customClass={"mr-12"}
          />
        </div>
      </div>
    </Modal>
  );
}

export default ItemsEditModal;
